const BASE = '/api'

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    ...options,
  })
  if (res.status === 401) {
    // Auth is enabled and session is missing/expired — redirect to Microsoft login
    window.location.href = '/auth/login'
    throw new Error('Unauthenticated')
  }
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json()
}

// Feed
export const getFeed = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  return request<FeedItem[]>(`/feed/${qs}`)
}

export const getDashboard = () => request<Dashboard>('/feed/dashboard')

export const actOnFeedItem = (id: number, action: string) =>
  request(`/feed/${id}/action`, {
    method: 'POST',
    body: JSON.stringify({ action }),
  })

export const markSeen = (id: number) =>
  request(`/feed/${id}/seen`, { method: 'POST' })

export const togglePin = (id: number) =>
  request(`/feed/${id}/pin`, { method: 'POST' })

export const ignoreSender = (id: number) =>
  request(`/feed/${id}/ignore-sender`, { method: 'POST' })

// Actions
export const getActions = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  return request<ActionItem[]>(`/actions/${qs}`)
}

export const updateAction = (id: number, update: Record<string, any>) =>
  request(`/actions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(update),
  })

export const toggleActionPin = (id: number) =>
  request(`/actions/${id}/pin`, { method: 'POST' })

// Contacts
export const getContacts = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  return request<Contact[]>(`/contacts/${qs}`)
}

export const toggleContactPin = (id: number) =>
  request(`/contacts/${id}/pin`, { method: 'POST' })

// System
export const triggerFullCycle = () =>
  request('/system/full-cycle', { method: 'POST' })

// Types
export interface FeedItem {
  id: number
  item_type: string
  urgency: string
  headline: string
  body: string | null
  context: Record<string, any> | null
  seen: boolean
  acted_on: boolean
  pinned: boolean
  event_at: string
  created_at: string
  client_name: string | null
}

export interface Dashboard {
  unseen_count: number
  critical_count: number
  action_items_open: number
  action_items_waiting: number
  action_items_stale: number
  contacts_overdue: number
  recent_items: FeedItem[]
}

export interface ActionItem {
  id: number
  title: string
  description: string | null
  source_type: string
  owner: string
  waiting_on: string | null
  waiting_on_email: string | null
  status: string
  urgency: string
  due_date: string | null
  stale_after_days: number
  last_evidence_at: string | null
  agent_notes: string | null
  client_name: string | null
  created_at: string
  pinned: boolean
}

export interface Contact {
  id: number
  name: string
  email: string | null
  phone: string | null
  title: string | null
  company: string | null
  role: string | null
  relationship_tier: string
  target_cadence_days: number | null
  last_contact_date: string | null
  last_contact_type: string | null
  interaction_count: number
  source: string
  confirmed: boolean
  pinned: boolean
  client_name: string | null
  days_since_contact: number | null
  cadence_status: string | null
}

// Chat
export interface ChatMessage {
  id: number
  role: 'user' | 'assistant'
  content: string
  entities_referenced: Record<string, number[]> | null
  created_at: string
  draft?: Draft  // set if this message triggered a draft
  images?: string[]  // Base64 data URIs for pasted images
}

export interface ChatSession {
  id: number
  title: string | null
  model: string | null
  last_message_at: string | null
  created_at: string
}

export interface ChatResponse {
  session_id: number
  response: string
  entities_referenced: Record<string, number[]> | null
}

export const sendChatMessage = (message: string, sessionId?: number) =>
  request<ChatResponse>('/chat/', {
    method: 'POST',
    body: JSON.stringify({ message, session_id: sessionId ?? null }),
  })

export const getChatSessions = () =>
  request<{ count: number; sessions: ChatSession[] }>('/chat/sessions')

export const getSessionMessages = (sessionId: number) =>
  request<{ session_id: number; title: string | null; count: number; messages: ChatMessage[] }>(
    `/chat/sessions/${sessionId}/messages`
  )

// Drafts
export interface Draft {
  id: number
  trigger_type: string
  to_email: string
  to_name: string | null
  subject: string
  body: string
  edited_body: string | null
  status: string
  client_id: number | null
  created_at: string
}

export const getDraft = (id: number) => request<Draft>(`/drafts/${id}`)

export const editDraft = (id: number, editedBody: string, subject?: string) =>
  request<{ id: number; status: string }>(`/drafts/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ edited_body: editedBody, subject }),
  })

export const sendDraft = (id: number) =>
  request<{ id: number; status: string; note: string }>(`/drafts/${id}/send`, { method: 'POST' })

// Documents (OneDrive)
export interface OneDriveItem {
  name: string
  type: 'file' | 'folder'
  path: string
  size: number | null
  mime_type: string | null
  modified: string | null
  graph_id: string
  indexed: boolean
}

export interface Document {
  id: number
  name: string
  path: string | null
  size_bytes: number | null
  indexed_by: string | null
  index_requested_at: string | null
  client_name: string | null
  has_text: boolean
}

export const browseOneDrive = (path: string = '/') =>
  request<OneDriveItem[]>(`/documents/browse?path=${encodeURIComponent(path)}`)

export const searchOneDrive = (q: string) =>
  request<OneDriveItem[]>(`/documents/search-onedrive?q=${encodeURIComponent(q)}`)

export const getIndexedDocuments = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  return request<Document[]>(`/documents/${qs}`)
}

export const indexDocument = (path?: string, graphId?: string) =>
  request('/documents/index', {
    method: 'POST',
    body: JSON.stringify({ path, graph_id: graphId }),
  })

export const deleteDocument = (id: number) =>
  request(`/documents/${id}`, { method: 'DELETE' })

// UI context — what Vernon is looking at
export interface ChatContext {
  view?: string         // "feed" | "actions" | "contacts" | "documents" | "dashboard"
  item_type?: string    // "feed_item" | "action_item" | "contact" | "document" | "onedrive_file"
  item_id?: number
  onedrive_path?: string
  selected_items?: Array<{ type: string; id: number; label?: string }>  // bulk-checked items
}

// Streaming chat
export const updateSessionModel = (sessionId: number, model: string) =>
  request<{ session_id: number; model: string; display_name: string }>(`/chat/sessions/${sessionId}/model`, {
    method: 'PUT',
    body: JSON.stringify({ model }),
  })

export async function* streamChatMessage(
  message: string,
  sessionId?: number,
  context?: ChatContext | null,
  images?: string[],
  model?: string | null,
): AsyncGenerator<{ type: string; content?: string; session_id?: number; entities_referenced?: any; draft_id?: number }> {
  const res = await fetch('/api/chat/stream', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ message, session_id: sessionId ?? null, context: context ?? null, images: images ?? null, model: model ?? null }),
  })
  if (res.status === 401) {
    window.location.href = '/auth/login'
    throw new Error('Unauthenticated')
  }
  if (!res.ok) throw new Error(`Chat error: ${res.status}`)
  const reader = res.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          yield JSON.parse(line.slice(6))
        } catch {}
      }
    }
  }
}
